import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { ApiEndpoint } from 'src/app/core/http/api';
import { ReusableService } from 'src/app/services/reusable.service';

/*
 * DTO của `POST org-context/get-context-catalog` (GetContextCatalogHandler.cs, camelCase).
 * Viết theo mã handler, không đoán: sai tên trường là `undefined` im lặng, không phải lỗi.
 */

export interface IContextCatalogTenant {
  id: string;
  code: string;
  name: string;
  displayName: string | null;
  countryCode: string | null;
  currencyCode: string | null;
  /** Gói giấy phép; null khi không có giấy phép còn hạn hoặc không phải tenant hiện hành. */
  plan: string | null;
  isCurrent: boolean;
}

export interface IContextCatalogBusinessUnit {
  id: string;
  code: string;
  name: string;
  displayName: string | null;
  /** Cha nằm ngoài danh sách ⇒ hiển thị như gốc. */
  parentBusinessUnitId: string | null;
  /** 1 Tập đoàn · 2 Pháp nhân · 3 Chi nhánh · 4 Nhãn hàng · 5 Kênh · 6 Nhà máy · 7 Dự án. */
  businessUnitTypeEnum: number;
  isLegalEntity: boolean;
  countryCode: string | null;
  currencyId: string;
  currencyCode: string | null;
  /** Cơ sở đang hoạt động, trực thuộc, trong phạm vi người gọi. */
  facilityCount: number;
  isActive: boolean;
  isDefault: boolean;
}

export interface IContextCatalogFacility {
  id: string;
  code: string;
  name: string;
  displayName: string | null;
  facilityTypeEnum: number;
  businessUnitId: string;
  isActive: boolean;
}

export interface IContextCatalogSelection {
  businessUnitIds: string[];
  effectiveBusinessUnitIds: string[];
  facilityIds: string[];
}

export interface IContextCatalogReportingCurrency {
  id: string;
  code: string;
  name: string | null;
  sourceCurrencyCodes: string[];
  isConverted: boolean;
}

export interface IContextCatalogFiscalPeriod {
  id: string;
  /** null = kỳ CHUNG của tenant; có giá trị = kỳ riêng của đơn vị. */
  businessUnitId: string | null;
  fiscalYear: number;
  periodNumber: number;
  periodCode: string;
  periodName: string;
  startDate: string;
  endDate: string;
  /** FiscalPeriodStatus: 1 Open · 2 SoftClose · 3 HardClose. */
  statusEnum: number;
  isOpen: boolean;
}

export interface IContextCatalog {
  currentTenantId: string | null;
  tenants: IContextCatalogTenant[];
  businessUnits: IContextCatalogBusinessUnit[];
  facilities: IContextCatalogFacility[];
  defaultBusinessUnitId: string | null;
  selection: IContextCatalogSelection;
  reportingCurrency: IContextCatalogReportingCurrency | null;
  currentFiscalPeriods: IContextCatalogFiscalPeriod[];
}

/**
 * Nạp danh mục cho panel "Bối cảnh làm việc" bằng MỘT lời gọi.
 *
 * Trả `null` khi lời gọi hỏng (404 lúc FE lên trước BE, mất mạng, 403…). `null` nghĩa là panel
 * rơi về ba danh sách cũ VÀ hợp đồng header ĐƠN — máy chủ cũ coi danh sách ngăn dấu phẩy là Guid
 * hỏng và bỏ cả header, tức là lặng lẽ hợp nhất toàn tenant. Endpoint nằm trong
 * SELF_HANDLED_URLS nên lỗi không bật toast.
 */
@Injectable({ providedIn: 'root' })
export class WorkingContextCatalogService {
  private _reusableService = inject(ReusableService);

  load(): Observable<IContextCatalog | null> {
    return this._reusableService.postTyped<IContextCatalog>(ApiEndpoint.GetContextCatalog, {}).pipe(
      map((res) => res?.result ?? null),
      catchError(() => of(null)),
    );
  }
}
